import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { XMarkIcon, ExclamationTriangleIcon, UserGroupIcon, DocumentTextIcon } from '@heroicons/react/24/outline';

interface UserDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reassignToUserId?: string) => void;
  userId: string;
  userName: string;
}

interface UserDependencies {
  user: {
    id: string;
    name: string;
    email: string;
  };
  dependencies: {
    cases: number;
    groups: Array<{
      id: string;
      name: string;
    }>;
  };
  canDelete: boolean;
}

const UserDeleteModal: React.FC<UserDeleteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  userId,
  userName
}) => {
  console.log('UserDeleteModal rendered:', { isOpen, userId, userName });

  const [selectedAssignmentGroup, setSelectedAssignmentGroup] = useState('');
  const [selectedReassignUser, setSelectedReassignUser] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch user dependencies
  const { data: dependencies, isLoading: loadingDependencies } = useQuery<UserDependencies>({
    queryKey: ['user-dependencies', userId],
    queryFn: async () => {
      const response = await api.get(`/users/${userId}/dependencies`);
      return response.data;
    },
    enabled: isOpen && !!userId
  });

  // Fetch assignment groups for the dropdown
  const { data: assignmentGroupsData } = useQuery({
    queryKey: ['assignment-groups'],
    queryFn: async () => {
      const response = await api.get('/groups/assignment-groups');
      return response.data;
    },
    enabled: isOpen && !!dependencies && dependencies.dependencies.cases > 0
  });

  // Fetch users based on selected assignment group
  const { data: usersData } = useQuery({
    queryKey: ['users-by-group', selectedAssignmentGroup],
    queryFn: async () => {
      if (!selectedAssignmentGroup) {
        // If no group selected, fetch all active users
        const response = await api.get('/users/dropdown?active=true');
        return response.data;
      }
      // Fetch users in the selected group
      const response = await api.get(`/users/by-group/${selectedAssignmentGroup}?active=true`);
      return response.data;
    },
    enabled: isOpen && !!dependencies && dependencies.dependencies.cases > 0
  });

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedAssignmentGroup('');
      setSelectedReassignUser('');
      setIsDeleting(false);
    }
  }, [isOpen]);

  // Reset selected user when assignment group changes
  useEffect(() => {
    setSelectedReassignUser('');
  }, [selectedAssignmentGroup]);

  if (!isOpen) return null;

  const handleConfirmDelete = async () => {
    // If there are active cases and no reassignment user selected, prevent deletion
    if (dependencies && dependencies.dependencies.cases > 0 && !selectedReassignUser) {
      return;
    }

    setIsDeleting(true);
    try {
      await onConfirm(selectedReassignUser || undefined);
    } finally {
      setIsDeleting(false);
    }
  };

  const hasActiveCases = dependencies && dependencies.dependencies.cases > 0;
  const canProceed = !hasActiveCases || (hasActiveCases && selectedReassignUser);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Delete User</h3>
              <p className="text-sm text-gray-600">{userName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {loadingDependencies ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : dependencies ? (
            <>
              {/* Warning message */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  {hasActiveCases
                    ? 'This user has active assignments. You must reassign them before deletion.'
                    : 'Are you sure you want to delete this user? This action cannot be undone.'}
                </p>
              </div>

              {/* Active Cases */}
              {dependencies.dependencies.cases > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <DocumentTextIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-blue-900 mb-2">
                        Active Cases/Incidents
                      </h4>
                      <p className="text-sm text-blue-700 mb-3">
                        This user has {dependencies.dependencies.cases} active case(s)/incident(s) assigned.
                      </p>

                      {/* Assignment Group Filter */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-blue-900 mb-2">
                          Filter by Assignment Group (Optional):
                        </label>
                        <select
                          value={selectedAssignmentGroup}
                          onChange={(e) => setSelectedAssignmentGroup(e.target.value)}
                          className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">All Users</option>
                          {assignmentGroupsData?.map((group: any) => (
                            <option key={group.id} value={group.id}>
                              {group.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Reassignment Dropdown */}
                      <div>
                        <label className="block text-sm font-medium text-blue-900 mb-2">
                          Reassign to: <span className="text-red-600">*</span>
                        </label>
                        <select
                          value={selectedReassignUser}
                          onChange={(e) => setSelectedReassignUser(e.target.value)}
                          className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select a user...</option>
                          {usersData?.users
                            ?.filter((u: any) => u.id !== userId) // Exclude the user being deleted
                            .map((u: any) => (
                              <option key={u.id} value={u.id}>
                                {u.name} ({u.email})
                              </option>
                            ))}
                        </select>
                        {!selectedReassignUser && (
                          <p className="mt-2 text-sm text-red-600">
                            Please select a user to reassign the cases before deleting.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Group Memberships */}
              {dependencies.dependencies.groups.length > 0 && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <UserGroupIcon className="h-5 w-5 text-purple-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-purple-900 mb-2">
                        Group Memberships
                      </h4>
                      <p className="text-sm text-purple-700 mb-2">
                        This user is a member of the following groups:
                      </p>
                      <ul className="list-disc list-inside space-y-1">
                        {dependencies.dependencies.groups.map(group => (
                          <li key={group.id} className="text-sm text-purple-800">
                            {group.name}
                          </li>
                        ))}
                      </ul>
                      <p className="text-sm text-purple-600 mt-2">
                        User will be removed from all groups upon deletion.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* No dependencies message */}
              {!hasActiveCases && dependencies.dependencies.groups.length === 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-600">
                    This user has no active assignments or group memberships.
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">
                Failed to load user dependencies. Please try again.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmDelete}
            disabled={!canProceed || isDeleting || loadingDependencies}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isDeleting ? 'Deleting...' : 'Delete User'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserDeleteModal;
